import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AnalysisResult } from "@/lib/api";
import { exportToPdf } from "@/lib/pdf-export";
import { FaDownload, FaCheckCircle, FaExclamationTriangle, FaQuestionCircle, FaInfoCircle } from "react-icons/fa";

interface AnalysisCardProps {
  result: AnalysisResult;
  isLoading?: boolean;
}

export default function AnalysisCard({ result, isLoading = false }: AnalysisCardProps) {
  // Defensive check for the result structure
  if (!result || typeof result.type !== 'string' || typeof result.confidence !== 'number' || typeof result.explanation !== 'string' || !Array.isArray(result.suggestions)) {
    console.error("AnalysisCard received invalid 'result' prop:", result);
    return (
      <Card className="border-2 border-red-500/30 bg-red-500/5 p-4">
        <CardHeader>
          <CardTitle className="text-red-500">Error Displaying Analysis</CardTitle>
        </CardHeader>
        <CardContent>
          <p>The analysis data is not in the expected format. Please try the analysis again.</p>
          <p className="text-xs text-gray-400 mt-2">Details: Received an unexpected data structure.</p>
        </CardContent>
      </Card>
    );
  }

  const handleExport = () => {
    exportToPdf(result, "Truth Nexus Analysis Report");
  };

  const getIcon = () => {
    switch (result.type) {
      case 'factual':
        return <FaCheckCircle className="h-8 w-8 text-green-500" />;
      case 'misinformation':
        return <FaExclamationTriangle className="h-8 w-8 text-red-500" />;
      case 'misleading':
        return <FaInfoCircle className="h-8 w-8 text-yellow-500" />;
      default:
        return <FaQuestionCircle className="h-8 w-8 text-gray-500" />;
    }
  };

  const getBackgroundClass = () => {
    switch (result.type) {
      case 'factual':
        return "border-green-500/30 bg-green-500/5";
      case 'misinformation':
        return "border-red-500/30 bg-red-500/5";
      case 'misleading':
        return "border-yellow-500/30 bg-yellow-500/5";
      default:
        return "border-gray-500/30 bg-gray-500/5";
    }
  };
  
  const getTitleStyle = () => {
    switch (result.type) {
      case 'factual':
        return "text-green-500";
      case 'misinformation':
        return "text-red-500";
      case 'misleading':
        return "text-yellow-500";
      default:
        return "text-gray-500";
    }
  };

  return (
    <Card className={`border-2 ${getBackgroundClass()} shadow-lg backdrop-blur-sm transition-all duration-300`}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="flex flex-col space-y-1.5">
          <CardTitle className={`text-2xl font-bold flex items-center gap-2 ${getTitleStyle()}`}>
            {getIcon()}
            {result.type.charAt(0).toUpperCase() + result.type.slice(1)} Content
          </CardTitle>
          <CardDescription>
            Confidence Level: {result.confidence}%
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="pt-4">
        <div className="space-y-4">
          <div className="rounded-md bg-gray-900/50 p-4 border border-gray-800">
            <h3 className="font-medium text-truth mb-2">Explanation</h3>
            <p className="text-sm text-gray-200">{result.explanation}</p>
          </div>
          
          <div>
            <h3 className="font-medium text-truth mb-2">Suggestions</h3>
            <ul className="list-disc list-inside space-y-1 text-sm">
              {result.suggestions.map((suggestion, index) => (
                <li key={index} className="text-gray-200">{suggestion}</li>
              ))}
            </ul>
          </div>
          
          {result.sourceAnalysis && (
            <div className="rounded-md bg-gray-900/50 p-4 border border-gray-800">
              <h3 className="font-medium text-truth mb-2">Source Credibility Analysis</h3>
              <div className="flex items-center mb-3">
                <div className="flex-1 h-3 bg-gray-700 rounded-full overflow-hidden">
                  <div 
                    className={`h-full ${
                      result.sourceAnalysis.credibility > 70 
                        ? 'bg-green-500' 
                        : result.sourceAnalysis.credibility > 40 
                          ? 'bg-yellow-500' 
                          : 'bg-red-500'
                    }`}
                    style={{ width: `${result.sourceAnalysis.credibility}%` }}
                  ></div>
                </div>
                <span className="ml-2 text-sm font-medium">{result.sourceAnalysis.credibility}%</span>
              </div>
              
              <div className="space-y-2">
                {result.sourceAnalysis.factors.map((factor, index) => (
                  <div key={index} className="text-sm">
                    <div className="flex justify-between items-center">
                      <span>{factor.name}</span>
                      <span className="font-medium">{factor.score}%</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">{factor.explanation}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </CardContent>
      <CardFooter className="border-t border-gray-800 pt-4">
        <Button 
          className="ml-auto flex gap-2 items-center"
          onClick={handleExport}
          disabled={isLoading}
        >
          <FaDownload className="h-4 w-4" />
          Export as PDF
        </Button>
      </CardFooter>
    </Card>
  );
}
